import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { reviewRulesApi, type ReviewRule } from '../features/reviewRules/api.ts';
import { usersApi, type OrgUserRow } from '../features/users/api.ts';
import { isAxiosError } from 'axios';

function createErrorMessage(e: unknown): string {
  if (isAxiosError(e)) {
    const code = (e.response?.data as { error?: string } | undefined)?.error;
    switch (code) {
      case 'rule_exists': return 'This person already has a review rule. Edit the existing one instead.';
      case 'sender_is_reviewer': return 'The reviewer must be a different person than the sender.';
      case 'reviewer_inactive': return 'That reviewer is deactivated — pick an active member.';
      case 'sender_inactive': return 'That sender is deactivated.';
      default: return 'Could not save the rule. Please try again.';
    }
  }
  return 'Could not save the rule. Please try again.';
}

function AddRuleForm({ members }: { members: OrgUserRow[] }): JSX.Element {
  const qc = useQueryClient();
  const [senderId, setSenderId] = useState('');
  const [reviewerId, setReviewerId] = useState('');

  const create = useMutation({
    mutationFn: () => reviewRulesApi.create(senderId, reviewerId),
    onSuccess: () => {
      setSenderId('');
      setReviewerId('');
      void qc.invalidateQueries({ queryKey: ['review-rules'] });
    },
  });

  const active = members.filter((m) => m.isActive);
  const canSubmit = senderId && reviewerId && senderId !== reviewerId;

  return (
    <form
      className="card-paper space-y-4 p-4"
      onSubmit={(e) => { e.preventDefault(); if (canSubmit) create.mutate(); }}
    >
      <h2 className="status-label text-muted-foreground flex items-center gap-2">
        <Icon name="add" size={15} /> Add a review rule
      </h2>
      <div className="grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
        <div className="space-y-1.5">
          <label htmlFor="rule-sender" className="field-label">When this person sends</label>
          <select
            id="rule-sender"
            value={senderId}
            onChange={(e) => setSenderId(e.target.value)}
            className="select"
          >
            <option value="">Select staff…</option>
            {active.map((m) => (
              <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
            ))}
          </select>
        </div>
        <div className="hidden sm:flex items-center justify-center pb-2.5 text-muted-foreground">
          <Icon name="arrow_forward" size={18} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="rule-reviewer" className="field-label">This person must approve first</label>
          <select
            id="rule-reviewer"
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
            className="select"
          >
            <option value="">Select reviewer…</option>
            {active.filter((m) => m.id !== senderId).map((m) => (
              <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" disabled={!canSubmit || create.isPending} className="btn-primary">
          {create.isPending ? 'Adding…' : 'Add rule'}
        </button>
        {create.isError && <span className="text-sm text-destructive">{createErrorMessage(create.error)}</span>}
      </div>
      <p className="text-xs text-muted-foreground">
        Documents this person sends will wait for the reviewer to approve before going out for signing.
        Documents pushed in from other Wapplify apps are not reviewed.
      </p>
    </form>
  );
}

function RuleRow({ rule }: { rule: ReviewRule }): JSX.Element {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['review-rules'] });
  const toggle = useMutation({
    mutationFn: () => reviewRulesApi.update(rule.id, { isActive: !rule.isActive }),
    onSuccess: () => { void invalidate(); },
  });
  const remove = useMutation({
    mutationFn: () => reviewRulesApi.remove(rule.id),
    onSuccess: () => { void invalidate(); },
  });

  const reviewerInactive = !rule.reviewer.isActive;

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="font-medium text-ink">{rule.sender.name}</span>
          <Icon name="arrow_forward" size={14} className="text-muted-foreground" />
          <span className="font-medium text-ink">{rule.reviewer.name}</span>
          {!rule.isActive && <span className="pill pill-neutral">Off</span>}
        </div>
        <div className="mono-meta text-muted-foreground mt-1">
          {rule.sender.email} reviewed by {rule.reviewer.email}
        </div>
        {reviewerInactive && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--warning-container))] px-2 py-1 text-xs text-[hsl(var(--on-warning-container))]">
            <Icon name="warning" size={13} />
            Reviewer is deactivated — this sender is blocked from sending until you fix it.
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending}
          className="btn-secondary btn-sm"
        >
          {rule.isActive ? 'Turn off' : 'Turn on'}
        </button>
        <button
          type="button"
          onClick={() => { if (window.confirm(`Remove the review rule for ${rule.sender.name}?`)) remove.mutate(); }}
          disabled={remove.isPending}
          aria-label={`Remove review rule for ${rule.sender.name}`}
          className="btn-ghost btn-sm inline-flex size-8 items-center justify-center !rounded-full !px-0 hover:text-[hsl(var(--on-error-container))]"
        >
          <Icon name="delete" size={15} />
        </button>
      </div>
    </li>
  );
}

export function ReviewFlow(): JSX.Element {
  const { data: rules, isLoading } = useQuery({ queryKey: ['review-rules'], queryFn: reviewRulesApi.list });
  const { data: usersData } = useQuery({ queryKey: ['users'], queryFn: usersApi.list });
  const members = useMemo(() => (usersData?.users ?? []).filter((u) => u.role !== 'SYS_ADMIN'), [usersData]);

  return (
    <div className="space-y-6">
      <section className="card-paper space-y-2 p-4">
        <h2 className="status-label text-muted-foreground flex items-center gap-2">
          <Icon name="verified_user" size={15} /> Review flow
        </h2>
        <p className="text-sm text-muted-foreground">
          Require certain staff to have their documents approved before they are sent for signing.
          The reviewer approves it (it goes out) or sends it back with a note. Only you (admins) set this up.
        </p>
      </section>

      <AddRuleForm members={members} />

      <section className="space-y-2">
        <h2 className="section-heading status-label">Current rules</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && (rules?.length ?? 0) === 0 && (
          <div className="card-paper p-8 text-center text-sm text-muted-foreground">
            No review rules yet. Everyone sends documents directly.
          </div>
        )}
        {!isLoading && rules && rules.length > 0 && (
          <ul className="card-paper divide-y divide-border/70 overflow-hidden">
            {rules.map((r) => <RuleRow key={r.id} rule={r} />)}
          </ul>
        )}
      </section>
    </div>
  );
}
